/**
 * Dashboard definitions. Two arrays per dashboard, and they belong to different
 * owners — which is worth knowing before deleting anything that looks unused.
 *
 * `layouts` is handed to **react-grid-layout verbatim** (WidgetGrid → GridLayout →
 * `<ResponsiveGridLayout layouts={layouts}>`). Its item keys are the library's
 * vocabulary, not ours:
 *
 *     w  h  x  y  i     position and span
 *     minW  minH        resize limits — the library enforces them
 *     static            pins the item; the library reads it
 *     moved             the library's own internal flag
 *
 * **None of those appear anywhere in uApp source, and all of them are live.** A
 * search for readers finds nothing and is the wrong test: the reader is the
 * library. Task 32.2 came one verification step from deleting 360 uses of this.
 *
 * `widgets` is ours. It never reaches the library — GridLayout maps over it to
 * render `<GridWidget>` children — so only `i` and `type` mean anything here.
 * Per-widget `isDraggable` / `isResizable` used to sit on these items and were
 * deleted by 32.2: drag and resize are a whole-dashboard edit mode held in
 * WidgetGrid state and toggled by `toggleEdit()`, so a per-widget flag read as
 * though one widget could be pinned while the others moved, and did nothing.
 */
export const widgetDashboardData =
{
    // A dashboard's `name` is its localStorage key. The ones that gained the
    // analytics widgets carry `-v2`: a layout saved under the old key does not
    // know the new widgets, and would have hidden them until "Reset layout".
    accounting: {
        name: 'accounting-Dashboard-v2',
        metadata: {
            title: 'accounting.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 2, h: 14, x: 0, y: 0,  i: '2', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 28, x: 2, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 14, x: 0, y: 14, i: '3', minW: 1, minH: 8, moved: false, static: false },
            ],
            md: [
                { w: 2, h: 13, x: 0, y: 0,  i: '2', minW: 1, minH: 8, moved: false, static: false },
                { w: 2, h: 14, x: 0, y: 13, i: '3', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 10, x: 0, y: 27, i: '1', minW: 1, minH: 1, moved: false, static: false },
            ],
            sm: [
                { w: 1, h: 13, x: 0, y: 0,  i: '2', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 14, x: 0, y: 13, i: '3', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 7,  x: 0, y: 27, i: '1', minW: 1, minH: 1, moved: false, static: false },
            ]
        },
        widgets:
            [
                { i: '1', type: 'journal.list.widget' },
                { i: '2', type: 'accounting.analytics.sales.expenses.widget' },
                { i: '3', type: 'accounting.analytics.result.bridge.widget' },
            ]
    },
    bank: {
        name:'bank-Dashboard',
        metadata: {
            title: 'bank.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 1, h: 13, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
            ],
            md: [
                { w: 1, h: 10, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
            ],
            sm: [
                { w: 1, h: 7, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
            ]
        },
        widgets:
            [
                { i: '1', type:'payment.account.list.widget' },
            ]
    },
    crm: {
        name:'crm-Dashboard-v2',
        metadata: {
            title: 'crm.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 1, h: 13, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 7, x: 1, y: 0, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 7, x: 2, y: 0, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 6, x: 2, y: 7, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 6, x: 1, y: 7, i: '5', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 14, x: 0, y: 13, i: '6', minW: 1, minH: 8, moved: false, static: false }],
            md: [
                { w: 1, h: 10, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 6, x: 1, y: 0, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 6, x: 0, y: 10, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 6, x: 1, y: 10, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 4, x: 1, y: 6, i: '5', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 14, x: 0, y: 16, i: '6', minW: 1, minH: 8, moved: false, static: false }],
            sm: [
                { w: 1, h: 7, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 0, y: 7, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 0, y: 12, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 0, y: 17, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 4, x: 0, y: 22, i: '5', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 14, x: 0, y: 26, i: '6', minW: 1, minH: 8, moved: false, static: false }]
        },
        widgets:
            [
                { i: '1', type:'contact.list.widget' },
                { i: '2', type:'crm.create.product.widget' },
                { i: '3', type:'go.home.widget' },
                { i: '4', type:'go.service.widget' },
                { i: '5', type:'crm.text.widget' },
                { i: '6', type:'crm.analytics.top.customers.widget' },
            ]
    },
    documents: {
        name: 'documents-Dashboard',
        metadata: {
            title: 'documents.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 1, h: 13, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false }
            ],
            md: [
                { w: 1, h: 10, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false }
            ],
            sm: [
                { w: 1, h: 7, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false }
            ]
        },
        widgets: [
            { i: '1', type: 'document.list.widget' }
        ]
    },
    inventory: {
        // v4: the stock value donuts, in place of the demo "Inventory Levels" chart
        name:'inventory-Dashboard-v4',
        metadata: {
            title: 'inventory.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            // Stock value by group where the demo chart was; top items under
            // the stock table, the two views of what is on hand side by side
            lg: [
                { w: 1, h: 16, x: 0, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 16, x: 1, y: 0,  i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 16, x: 2, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 2, y: 16, i: '6', minW: 1, minH: 8, moved: false, static: false },
                { w: 2, h: 10, x: 0, y: 16, i: '4', minW: 1, minH: 1, moved: false, static: false }
            ],
            md: [
                { w: 1, h: 13, x: 0, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 0,  i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 2, h: 16, x: 0, y: 13, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 13, x: 0, y: 29, i: '6', minW: 1, minH: 8, moved: false, static: false },
                { w: 2, h: 10, x: 0, y: 42, i: '4', minW: 1, minH: 1, moved: false, static: false }
            ],
            sm: [
                { w: 1, h: 7,  x: 0, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 14, x: 0, y: 7,  i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 16, x: 0, y: 21, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 14, x: 0, y: 37, i: '6', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 12, x: 0, y: 51, i: '4', minW: 1, minH: 1, moved: false, static: false }
            ]
        },
        widgets:
            [
                { i: '1', type:'product.list.widget' },
                { i: '3', type:'inventory.stock.table.widget' },
                { i: '4', type:'inventory.info.card.widget' },
                { i: '5', type:'inventory.analytics.value.group.widget' },
                { i: '6', type:'inventory.analytics.value.item.widget' },
            ]
    },
    manufacturing: {
        name: 'manufacturing-Dashboard-v2',
        metadata: {
            title: 'manufacturing.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 1, h: 13, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 0, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 2, y: 0, i: '3', minW: 1, minH: 8, moved: false, static: false }
            ],
            md: [
                { w: 1, h: 10, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 1, y: 0, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 12, x: 0, y: 10, i: '3', minW: 1, minH: 8, moved: false, static: false }
            ],
            sm: [
                { w: 1, h: 7, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 7, x: 0, y: 7, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 14, i: '3', minW: 1, minH: 8, moved: false, static: false }
            ]
        },
        widgets: [
            { i: '1', type: 'work.order.list.widget' },
            { i: '2', type: 'machine.list.widget' },
            { i: '3', type: 'manufacturing.analytics.work.orders.widget' }
        ]
    },
    project: {
        name: 'project-Dashboard',
        metadata: {
            title: 'project.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 3, h: 7,  x: 0, y: 0, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 7, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 7, i: '2', minW: 1, minH: 1, moved: false, static: false }
            ],
            md: [
                { w: 2, h: 7,  x: 0, y: 0, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 0, y: 7, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 1, y: 7, i: '2', minW: 1, minH: 1, moved: false, static: false }
            ],
            sm: [
                { w: 1, h: 7,  x: 0, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 7,  x: 0, y: 7,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 7,  x: 0, y: 14, i: '2', minW: 1, minH: 1, moved: false, static: false }
            ]
        },
        widgets: [
            { i: '1', type: 'project.list.widget' },
            { i: '2', type: 'project.task.sublist.widget' },
            { i: '3', type: 'project.totals.widget' },
        ]
    },
    purchases: {
        name:'purchases-Dashboard-v2',
        metadata: {
            title: 'purchases.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 3, h: 7,  x: 0, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 7,  i: '4', minW: 1, minH: 8, moved: false, static: false },
                { w: 2, h: 13, x: 1, y: 7,  i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 20, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 13, x: 1, y: 20, i: '2', minW: 1, minH: 1, moved: false, static: false },
            ],
            md: [
                { w: 2, h: 7,  x: 0, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 7,  i: '4', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 7,  i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 20, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 20, i: '2', minW: 1, minH: 1, moved: false, static: false },
            ],
            sm: [
                { w: 1, h: 7,  x: 0, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 14, x: 0, y: 7,  i: '4', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 12, x: 0, y: 21, i: '5', minW: 1, minH: 8, moved: false, static: false },
                { w: 1, h: 7,  x: 0, y: 33, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 40, i: '2', minW: 1, minH: 1, moved: false, static: false },
            ]
        },
        widgets: [
            { i: '1', type: 'purchase.order.list.widget' },
            { i: '2', type: 'purchases.expense.category.widget' },
            { i: '3', type: 'purchases.totals.widget' },
            { i: '4', type: 'purchases.analytics.spend.supplier.widget' },
            { i: '5', type: 'purchases.analytics.orders.placed.widget' },
        ]
    },
    sales: {
        // v4: the totals cards became tiles, taller on a phone; the invoiced
        // bar chart became an analytics chart, taller everywhere
        name:'sales-Dashboard-v4',
        metadata: {
            title: 'sales.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [16, 16],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { i: '1',  x: 2, y: 0,  w: 1, h: 14, minW: 1, minH: 1, moved: false, static: false },
                { i: '2',  x: 0, y: 41, w: 2, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '3',  x: 0, y: 0,  w: 2, h: 7,  minW: 1, minH: 1, moved: false, static: false },
                { i: '4',  x: 0, y: 7,  w: 2, h: 7,  minW: 1, minH: 1, moved: false, static: false },
                { i: '5',  x: 2, y: 41, w: 1, h: 10, minW: 1, minH: 1, moved: false, static: false },
                { i: '6',  x: 0, y: 14, w: 2, h: 13, minW: 1, minH: 6, moved: false, static: false },
                { i: '7',  x: 2, y: 14, w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '8',  x: 0, y: 27, w: 1, h: 14, minW: 1, minH: 8, moved: false, static: false },
                { i: '9',  x: 1, y: 27, w: 1, h: 14, minW: 1, minH: 8, moved: false, static: false },
                { i: '10', x: 2, y: 27, w: 1, h: 14, minW: 1, minH: 8, moved: false, static: false }
            ],
            md: [
                { i: '1',  x: 0, y: 53, w: 1, h: 10, minW: 1, minH: 1, moved: false, static: false },
                { i: '2',  x: 1, y: 53, w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '3',  x: 0, y: 0,  w: 1, h: 12, minW: 1, minH: 1, moved: false, static: false },
                { i: '4',  x: 1, y: 0,  w: 1, h: 12, minW: 1, minH: 1, moved: false, static: false },
                { i: '5',  x: 0, y: 63, w: 1, h: 10, minW: 1, minH: 1, moved: false, static: false },
                { i: '6',  x: 0, y: 12, w: 2, h: 13, minW: 1, minH: 6, moved: false, static: false },
                { i: '7',  x: 0, y: 25, w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '8',  x: 1, y: 25, w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '9',  x: 0, y: 38, w: 1, h: 15, minW: 1, minH: 8, moved: false, static: false },
                { i: '10', x: 1, y: 38, w: 1, h: 15, minW: 1, minH: 8, moved: false, static: false }
            ],
            sm: [
                { i: '1',  x: 0, y: 89,  w: 1, h: 7,  minW: 1, minH: 1, moved: false, static: false },
                { i: '2',  x: 0, y: 96,  w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '3',  x: 0, y: 0,   w: 1, h: 11, minW: 1, minH: 1, moved: false, static: false },
                { i: '4',  x: 0, y: 11,  w: 1, h: 11, minW: 1, minH: 1, moved: false, static: false },
                { i: '5',  x: 0, y: 105, w: 1, h: 12, minW: 1, minH: 1, moved: false, static: false },
                { i: '6',  x: 0, y: 22,  w: 1, h: 13, minW: 1, minH: 6, moved: false, static: false },
                { i: '7',  x: 0, y: 35,  w: 1, h: 12, minW: 1, minH: 8, moved: false, static: false },
                { i: '8',  x: 0, y: 47,  w: 1, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '9',  x: 0, y: 60,  w: 1, h: 15, minW: 1, minH: 8, moved: false, static: false },
                { i: '10', x: 0, y: 75,  w: 1, h: 14, minW: 1, minH: 8, moved: false, static: false }
            ]
        },
        widgets:
            [
                { i: '1', type:'contact.list.widget' },
                { i: '2', type:'sales.invoiced.monthly.widget' },
                { i: '3', type:'sales.totals.widget' },
                { i: '4', type:'sales.net.sales.widget' },
                { i: '5', type:'sales.info.card.widget' },
                { i: '6', type:'sales.analytics.net.sales.widget' },
                { i: '7', type:'sales.analytics.paid.share.widget' },
                { i: '8', type:'sales.analytics.top.customers.widget' },
                { i: '9', type:'sales.analytics.activity.widget' },
                { i: '10', type:'sales.analytics.recent.invoices.widget' },
            ]
    },
    service: {
        name: 'service-Dashboard-v2',
        metadata: {
            title: 'service.dashboard.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 1 },
            offset: 24,
            margin: { lg: [16, 16], md: [16, 16], sm: [16, 16] },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { i: '5', x: 0, y: 0,  w: 2, h: 14, minW: 1, minH: 8, moved: false, static: false },
                { i: '2', x: 2, y: 0,  w: 1, h: 14, minW: 1, minH: 1, moved: false, static: false },
                { i: '1', x: 0, y: 14, w: 2, h: 13, minW: 1, minH: 1, moved: false, static: false },
                { i: '4', x: 2, y: 14, w: 1, h: 13, minW: 1, minH: 1, moved: false, static: false },
                { i: '3', x: 0, y: 27, w: 2, h: 11, minW: 1, minH: 1, moved: false, static: false }
            ],
            md: [
                { i: '5', x: 0, y: 0,  w: 2, h: 13, minW: 1, minH: 8, moved: false, static: false },
                { i: '1', x: 0, y: 13, w: 1, h: 10, minW: 1, minH: 1, moved: false, static: false },
                { i: '2', x: 1, y: 13, w: 1, h: 10, minW: 1, minH: 1, moved: false, static: false },
                { i: '3', x: 0, y: 23, w: 1, h: 9,  minW: 1, minH: 1, moved: false, static: false },
                { i: '4', x: 1, y: 23, w: 1, h: 9,  minW: 1, minH: 1, moved: false, static: false }
            ],
            sm: [
                { i: '5', x: 0, y: 0,  w: 1, h: 14, minW: 1, minH: 8, moved: false, static: false },
                { i: '1', x: 0, y: 14, w: 1, h: 8,  minW: 1, minH: 1, moved: false, static: false },
                { i: '2', x: 0, y: 22, w: 1, h: 8,  minW: 1, minH: 1, moved: false, static: false },
                { i: '3', x: 0, y: 30, w: 1, h: 7,  minW: 1, minH: 1, moved: false, static: false },
                { i: '4', x: 0, y: 37, w: 1, h: 7,  minW: 1, minH: 1, moved: false, static: false }
            ]
        },
        widgets: [
            { i: '1', type: 'service.ticket.list.widget' },
            { i: '2', type: 'service.workOrder.list.widget' },
            { i: '3', type: 'service.asset.list.widget' },
            { i: '4', type: 'service.text.widget' },
            { i: '5', type: 'service.analytics.tickets.priority.widget' }
        ]
    },
    warehouse: {
        name: 'warehouse-Dashboard',
        metadata: {
            title: 'warehouse.overview.title',
            resetButton: 'all.dashboard.reset.button',
            editButton: 'all.dashboard.edit.button'
        },
        setup: {
            breakpoints: { lg: 1200, md: 768, sm: 480 },
            cols: { lg: 3, md: 2, sm: 2 },
            offset: 24,
            margin: {
                lg: [16, 16],
                md: [16, 16],
                sm: [8, 8],
            },
            rowHeight: 16
        },
        layouts: {
            lg: [
                { w: 1, h: 13, x: 0, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 0,  i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 2, y: 0,  i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 0, y: 13, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 13, x: 1, y: 13, i: '5', minW: 1, minH: 1, moved: false, static: false }
            ],
            md: [
                { w: 1, h: 10, x: 0, y: 0,  i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 1, y: 0,  i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 0, y: 10, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 10, x: 1, y: 10, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 10, x: 0, y: 20, i: '5', minW: 1, minH: 1, moved: false, static: false }
            ],
            sm: [
                { w: 1, h: 5, x: 0, y: 0, i: '1', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 1, y: 0, i: '2', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 0, y: 5, i: '3', minW: 1, minH: 1, moved: false, static: false },
                { w: 1, h: 5, x: 1, y: 5, i: '4', minW: 1, minH: 1, moved: false, static: false },
                { w: 2, h: 5, x: 0, y: 10,i: '5', minW: 1, minH: 1, moved: false, static: false }
            ]
        },
        widgets: [
            { i: '1', type: 'warehouse.pick.widget' },
            { i: '2', type: 'warehouse.putaway.widget' },
            { i: '3', type: 'warehouse.transfer.widget' },
            { i: '4', type: 'warehouse.return.widget' },
            { i: '5', type: 'warehouse.quarantine.widget' }
        ]
    },
}